import { cookieManager } from "@/service/cookieManager";

const state = {
	elaborationStationViewOptions: {
		showChoices: false,
		orderNewestFirst: false,
		isFormatedTicket: false,
		showProductsUngrouped: false,
		showGhostProductOrders: true,
		showGhostOrdersProductOrders: false,
		showOtherKitchenAccounts: true,
	},
};

/** When true, kitchen/bar elaboration getters use only venue params (no cookie overrides). */
function isElaborationViewEnforced(rootState, rootGetters) {
	const v = rootGetters["copilot/view"];
	if (v !== "Cocina" && v !== "Barra") return false;
	const c = rootState.venue.copilotConfig;
	if (!c) return true;
	if (v === "Cocina") return c.copilotElaborationKitchenEnforce !== false;
	if (v === "Barra") return c.copilotElaborationBarEnforce !== false;
	return true;
}

function elaborationViewParams(rootState, rootGetters) {
	const c = rootState.venue.copilotConfig;
	const isBar = rootGetters["copilot/view"] === "Barra";
	if (!c) {
		return {
			productOrderOptions: null,
			newFirst: null,
			style: null,
			productOrderGroup: null,
		};
	}
	if (isBar) {
		return {
			productOrderOptions: c.copilotElaborationBarProductOrderOptions ?? c.copilotElaborationProductOrderOptions,
			newFirst: c.copilotElaborationBarNewFirst ?? c.copilotElaborationNewFirst,
			style: c.copilotElaborationBarStyle ?? c.copilotElaborationStyle,
			productOrderGroup: c.copilotElaborationBarProductOrderGroup ?? c.copilotElaborationProductOrderGroup,
		};
	}
	return {
		productOrderOptions: c.copilotElaborationProductOrderOptions,
		newFirst: c.copilotElaborationNewFirst,
		style: c.copilotElaborationStyle,
		productOrderGroup: c.copilotElaborationProductOrderGroup,
	};
}

const getters = {
	elaborationStationViewOptions(state) {
		return state.elaborationStationViewOptions;
	},
	showChoices(state, getters, rootState, rootGetters) {
		if (!isElaborationViewEnforced(rootState, rootGetters)) {
			return state.elaborationStationViewOptions.showChoices;
		}
		const opt = elaborationViewParams(rootState, rootGetters).productOrderOptions;
		return opt === "show";
	},
	orderNewestFirst(state, getters, rootState, rootGetters) {
		if (!isElaborationViewEnforced(rootState, rootGetters)) {
			return state.elaborationStationViewOptions.orderNewestFirst;
		}
		const n = elaborationViewParams(rootState, rootGetters).newFirst;
		return n === 1;
	},
	isFormatedTicket(state, getters, rootState, rootGetters) {
		if (!isElaborationViewEnforced(rootState, rootGetters)) {
			return state.elaborationStationViewOptions.isFormatedTicket;
		}
		const style = elaborationViewParams(rootState, rootGetters).style;
		return style === "vertical";
	},
	showProductsUngrouped(state, getters, rootState, rootGetters) {
		if (!isElaborationViewEnforced(rootState, rootGetters)) {
			return state.elaborationStationViewOptions.showProductsUngrouped;
		}
		const group = elaborationViewParams(rootState, rootGetters).productOrderGroup;
		return group === 1 || group === "1";
	},
	showGhostProductOrders(state) {
		return state.elaborationStationViewOptions.showGhostProductOrders;
	},
	showGhostOrdersProductOrders(state) {
		return state.elaborationStationViewOptions.showGhostOrdersProductOrders;
	},
	showOtherKitchenAccounts(state) {
		return state.elaborationStationViewOptions.showOtherKitchenAccounts;
	},
};

const mutations = {
	setShowChoices(state, value) {
		state.elaborationStationViewOptions.showChoices = value;
	},
	setOrderNewestFirst(state, value) {
		state.elaborationStationViewOptions.orderNewestFirst = value;
	},
	setIsFormatedTicket(state, value) {
		state.elaborationStationViewOptions.isFormatedTicket = value;
	},
	setShowProductsUngrouped(state, value) {
		state.elaborationStationViewOptions.showProductsUngrouped = value;
	},
	setShowGhostProductOrders(state, value) {
		state.elaborationStationViewOptions.showGhostProductOrders = value;
	},
	setShowGhostOrdersProductOrders(state, value) {
		state.elaborationStationViewOptions.showGhostOrdersProductOrders = value;
	},
	setShowOtherKitchenAccounts(state, value) {
		state.elaborationStationViewOptions.showOtherKitchenAccounts = value;
	},
};

const actions = {
	updateShowChoices({ commit, dispatch }, value) {
		commit("setShowChoices", value);
		dispatch("saveViewOptionsToCookie");
	},
	updateOrderNewestFirst({ commit, dispatch }, value) {
		commit("setOrderNewestFirst", value);
		dispatch("saveViewOptionsToCookie");
	},
	updateIsFormatedTicket({ commit, dispatch }, value) {
		commit("setIsFormatedTicket", value);
		dispatch("saveViewOptionsToCookie");
	},
	updateShowProductsUngrouped({ commit, dispatch }, value) {
		commit("setShowProductsUngrouped", value);
		dispatch("saveViewOptionsToCookie");
	},
	updateShowGhostProductOrders({ commit, dispatch }, value) {
		commit("setShowGhostProductOrders", value);
		dispatch("saveViewOptionsToCookie");
	},
	updateShowGhostOrdersProductOrders({ commit, dispatch }, value) {
		commit("setShowGhostOrdersProductOrders", value);
		dispatch("saveViewOptionsToCookie");
	},
	updateShowOtherKitchenAccounts({ commit, dispatch }, value) {
		commit("setShowOtherKitchenAccounts", value);
		dispatch("saveViewOptionsToCookie");
	},
	initializeViewOptionsFromCookie({ commit }) {
		const cookie = cookieManager.getCookie("elaborationStationViewOptions");
		if (!cookie) return;

		try {
			const options = JSON.parse(cookie);
			if (typeof options === "object") {
				if ("showChoices" in options) commit("setShowChoices", options.showChoices);
				if ("orderNewestFirst" in options) commit("setOrderNewestFirst", options.orderNewestFirst);
				if ("isFormatedTicket" in options) commit("setIsFormatedTicket", options.isFormatedTicket);
				if ("showProductsUngrouped" in options) commit("setShowProductsUngrouped", options.showProductsUngrouped);
				if ("isIntern" in options) commit("setIsIntern", options.isIntern);
				if ("showGhostProductOrders" in options) {
					commit("setShowGhostProductOrders", options.showGhostProductOrders);
				}
				if ("showGhostOrdersProductOrders" in options) {
					commit("setShowGhostOrdersProductOrders", options.showGhostOrdersProductOrders);
				}
				if ("showOtherKitchenAccounts" in options) {
					commit("setShowOtherKitchenAccounts", options.showOtherKitchenAccounts);
				}
			}
		} catch (err) {
			console.warn("Cookie malformada: elaborationStationViewOptions", err);
		}
	},
	saveViewOptionsToCookie({ state }) {
		cookieManager.setCookie("elaborationStationViewOptions", JSON.stringify(state.elaborationStationViewOptions));
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
